import 'whatwg-fetch';
import merge from 'lodash/merge';
import { getHeadersOptions } from './getHeaderOptions';
import { nextAbortSignal } from './abortRegistry';

// Remove this ASAP, Cache-Control + Pragma should do the job
const addCacheBusterToInput = (input: RequestInfo) => {
  if (typeof input === 'string') {
    return `${input}${/[?]/.test(input) ? '&' : '?'}cb=${new Date().getTime()}`;
  }

  return {
    ...input,
    url: `${input.url}${/[?]/.test(input.url) ? '&' : '?'}cb=${new Date().getTime()}`,
  };
};

export const addTpxFlagToInput = (input: RequestInfo) => {
  if (!ADV.data.tpx) {
    return input;
  }

  if (typeof input === 'string') {
    return `${input}${/[?]/.test(input) ? '&' : '?'}tpx=true`;
  }

  return {
    ...input,
    url: `${input.url}${/[?]/.test(input.url) ? '&' : '?'}tpx=true`,
  };
};

export const addPreviewFlagToInput = (input: RequestInfo) => {
  if (!ADV.data.cx) {
    return input;
  }

  if (typeof input === 'string') {
    return `${input}${/[?]/.test(input) ? '&' : '?'}cx=true`;
  }

  return {
    ...input,
    url: `${input.url}${/[?]/.test(input.url) ? '&' : '?'}cx=true`,
  };
};

export const addOrionUserIdToInput = (input: RequestInfo) => {
  if (!ADV.data.oid) {
    return input;
  }

  if (typeof input === 'string') {
    return `${input}${/[?]/.test(input) ? '&' : '?'}oid=${encodeURIComponent(ADV.data.oid)}`;
  }

  return {
    ...input,
    url: `${input.url}${/[?]/.test(input.url) ? '&' : '?'}oid=${encodeURIComponent(ADV.data.oid)}`,
  };
};

export const addCobrowsingFlagToInput = (input: RequestInfo) => {
  if (!ADV.cobrowsingOperator) {
    return input;
  }

  if (typeof input === 'string') {
    return `${input}${/[?]/.test(input) ? '&' : '?'}cobrowsingOperator=${ADV.cobrowsingOperator}`;
  }

  return {
    ...input,
    url: `${input.url}${/[?]/.test(input.url) ? '&' : '?'}cobrowsingOperator=${
      ADV.cobrowsingOperator
    }`,
  };
};

/**
 * @deprecated
 *
 * Do not call this directly, please use authFetch instead ( commonUtils/authFetch )
 * Do not remove, authFetch depends on this.
 *
 */
const fetchWithDefaults = async (
  input: RequestInfo,
  init?: RequestInit & { extendSession?: boolean; showSpinner?: boolean; abortKey?: string },
) => {
  const inputWithCacheBuster = addCacheBusterToInput(
    addTpxFlagToInput(addPreviewFlagToInput(addOrionUserIdToInput(addCobrowsingFlagToInput(input)))),
  );

  const { xsrf, authorization } = getHeadersOptions();

  const { abortKey, signal: explicitSignal, ...restInit } = init ?? {};

  // When an abortKey is provided (and no explicit signal), register a fresh
  // signal that aborts any previous in-flight request sharing the same key.
  // Kept out of the lodash merge below, which would deep-traverse an AbortSignal.
  const signal = explicitSignal ?? (abortKey ? nextAbortSignal(abortKey) : undefined);

  const options = merge(
    {
      extendSession: true,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        Pragma: 'no-cache',
        'X-Requested-With': 'XMLHttpRequest',
        'Cache-Control': 'no-cache',
        'x-xsrf-token': xsrf,
        ...(authorization ? { Authorization: authorization } : {}),
      },
    },
    restInit,
  );

  let spinner: any;

  if (restInit.showSpinner) {
    spinner = new ADV.util.Spinner();
  }

  // Attach the signal here rather than via merge, which would deep-traverse an AbortSignal.
  // The controller is intentionally not cleared here: fetch() resolves once the response
  // headers arrive, but callers still consume the body (e.g. response.json()). Clearing now
  // would let a superseding same-key request find nothing to abort, so a stale older response
  // could finish and overwrite fresher data. The registry supersedes on its own via
  // nextAbortSignal (abort + replace) and abortRequest (unmount/tab change).
  const response = await fetch(inputWithCacheBuster, signal ? { ...options, signal } : options);

  if (spinner) {
    spinner.destroy();
  }

  switch (response.status) {
    case 401:
      if (
        ADV.util.env('isAdviserArea') &&
        response.headers.get('X-Adv-Reason') === 'License in use'
      ) {
        window.ADV.adviser.showMultipleSessionWarningModal();
        return response;
      }

      ADV.Controller.logout(false, true);

      break;
    case 402:
      if (ADV.util.Router.status().id !== 'Checkout') {
        ADV.util.Router.go2('Checkout');
        return response;
      }

      break;

    case 503: // Maintenance
      window.location.href = '/Maintenance';
      return response;
    default:
  }

  if (response.headers.get('X-Slided') === '1' && options.extendSession) {
    ADV.util.SessionTimeout.extendTimer();
  }

  const sidRenewToken = response.headers.get('X-Adv-Sid-Renew');
  const xsrfRenewToken = response.headers.get('X-Xsrf-Token-Renew');

  if (sidRenewToken) {
    sessionStorage.setItem('sid', sidRenewToken);
  }

  if (xsrfRenewToken) {
    sessionStorage.setItem('xsrf', xsrfRenewToken);
  }

  return response;
};

export default fetchWithDefaults;
